/**
 * What more than one of a counting unit is called.
 *
 * ## Why this is not a rule
 *
 * A short delivery read *"3 bottle short of the 10 bottle ordered"*, and so did every other
 * quantity on every screen. `formatQty` is the one place that prints a quantity, which makes the
 * one-line fix look obvious: add an "s" when the number is not one.
 *
 * It is the wrong fix and it cannot be corrected later. The unit is **free text the owner types**.
 * This bar counts limes in `kg`, rum in `bottle`, beer in `case` and cooking oil in `L`. The rule
 * that gets "bottles" right gets "2 kgs" and "3 Ls" wrong, on the units used most, in a bar where
 * a manager reading a wrong-looking number stops believing the column it is in.
 *
 * So the platform asks — once, for the WORD rather than for the item — and stores the answer in
 * `unit_names`. Where nobody has answered, the singular is printed for every quantity, which is
 * exactly what happened before this existed: clumsy, and right, where a guess would be wrong.
 *
 * ## Why a module-level registry rather than a parameter
 *
 * `formatQty` is called from twenty places, in tables, in confirmation sentences and inside toast
 * copy, and threading a plural through all of them means twenty call sites that can each forget it
 * — which is the same shape of defect as the four hand-written quantity formatters that made
 * `formatQty` the one place to begin with. The answers are a small, stable, business-wide lookup
 * with no per-screen variation, so they are loaded once by `useUnitNames` near the root and read
 * synchronously here.
 *
 * Before that query lands the map is empty and every unit falls back to its singular, which is a
 * correct reading rather than a wrong one — the failure mode of "not loaded yet" is the same as
 * the failure mode of "nobody has said".
 */

/** Keyed by the word folded to lower case: "Bottle" and "bottle" are one word, as the API says. */
let PLURALS = new Map<string, string>();

const key = (unit: string): string => unit.trim().toLowerCase();

/** Called by `useUnitNames` when the answers arrive. Replaces the lot rather than merging. */
export function setUnitPlurals(rows: readonly { unit: string; plural: string }[]): void {
  PLURALS = new Map(rows.map((r) => [key(r.unit), r.plural]));
}

/** What more than one of this unit is called, or the unit itself when nobody has said. */
export function pluralOf(unit: string): string {
  return PLURALS.get(key(unit)) ?? unit;
}

/**
 * The unit to print beside a quantity.
 *
 * Exactly one is singular. Nought is plural — "0 bottles" is how English counts nothing — and so
 * is every fraction, including 0.5: "0.5 kg" is right because the plural of "kg" is "kg", and
 * "0.5 bottles" is what a person says out loud.
 *
 * ## What this is deliberately NOT for
 *
 * The unit printed inside a number box — Mantine's `suffix` on "How much", on "Counted", on a
 * price per unit — stays singular and is not run through here. It is a LABEL saying what the box
 * is measured in, the same as "Price per bottle" above it, and a label that rewrites itself
 * between the "1" and the "2" of "12" is noise in the one place in this portal where the input
 * control has already cost real recorded amounts (see `NumberInputValue` in `lib/format.ts`).
 * Everything the reader is meant to READ — tables, sentences, toasts, confirmations — goes through
 * `formatQty`.
 */
export function unitFor(qty: number, unit: string): string {
  return qty === 1 ? unit : pluralOf(unit);
}

/**
 * ONE VOWEL-BEARING WORD looks like a noun; everything shorter or vowel-less is a measure's symbol.
 *
 * `kg`, `mL`, `lb`, `qt`, `tsp`, `oz` — the ones this bar actually types — are all caught by one of
 * three tests: no vowel, two letters or fewer, or a capital after the first letter. None of them
 * takes an "s", which is the half of the problem a naive rule gets wrong on the units used most.
 */
function looksLikeASymbol(word: string): boolean {
  if (word.length <= 2) return true;
  if (!/[aeiou]/i.test(word)) return true;
  if (/[A-Z]/.test(word.slice(1))) return true;
  // Digits or punctuation: "750ml", "1/2 case". Nothing here can be reasoned about.
  return /[^A-Za-z ]/.test(word);
}

/**
 * THE BOX'S OPENING ANSWER — never what gets printed.
 *
 * The platform must not GUESS a plural, and this is not that: `unitFor` still prints the singular
 * for any word nobody has answered for, and it always will. This is the value a form puts in the
 * box for the owner to read and correct before he saves — the difference between a suggestion he
 * accepts and a rule that decides behind him, and the reason a stock item added at seven in the
 * evening is not interrogated about English.
 *
 * Where somebody has already answered, that answer is what comes back: this is how the fortieth
 * item counted in bottles arrives with the box filled in and nobody thinking about it. Only a word
 * nobody has answered for gets the guess, and the guess is deliberately shallow — an "s", the
 * "-es"/"-ies" spellings that make it readable, and NOTHING for anything that looks like a measure.
 * "loaf" comes back "loafs" and the owner types "loaves" over it, which costs him one word and is
 * the trade this whole design is: correctable beats demanded.
 */
export function suggestPlural(unit: string | null | undefined): string {
  const word = unit?.trim();
  if (!word) return '';
  const answered = PLURALS.get(key(word));
  if (answered) return answered;
  if (looksLikeASymbol(word)) return word;
  if (/(s|x|z|ch|sh)$/i.test(word)) return `${word}es`;
  if (/[^aeiou]y$/i.test(word)) return `${word.slice(0, -1)}ies`;
  return `${word}s`;
}
